import classNames from "classnames";
import * as React from "react";

import { formatDuration } from "@/shared/time/formatDuration";

import { noneInSpan } from "./layout";
import { NoSessionsInSpan } from "./NoSessionsInSpan";
import { makeTickFormatter } from "./tickFormatter";
import { useTimelineContext } from "./TimelineContext";
import type { TimelineTrackItem } from "./TimelineTrackItem";
import styles from "./TimelineTracks.module.css";
import { TrackBar } from "./TrackBar";
import { TrackCluster } from "./TrackCluster";
import { TrackHoverCard } from "./TrackHoverCard";
import { percent } from "./trackStyle";
import { useElementWidth } from "./useElementWidth";
import { useTimelineGestures } from "./useTimelineGestures";
import { useTrackLayout } from "./useTrackLayout";
import { fitWindow, fractionOf } from "./window";

export interface TimelineTracksProps<T> {
  /** The selected item, drawn apart and never clustered. */
  selectedId: string | null;
  /** A bar click selects its item; a click on empty track space clears (`null`). Clusters stay internal. */
  onSelect?: (item: TimelineTrackItem<T> | null) => void;
  /** Every matching item, loaded or not: an empty window is only "none in span" when some exist. */
  totalCount: number;
  /** "Jump to nearest" in an empty window: show the closest item, earlier or later, without selecting it. */
  onJumpToNearest: () => void;
}

const formatIdle = (ms: number) => formatDuration(ms / 1000);

/**
 * The tracks: an axis, each item's bar on its track, cluster badges where
 * bars would overlap, and idle-gap labels. Positions are
 * fractions of the window drawn with CSS `%`; the measured width only sizes
 * the clustering and tick thresholds. Layout comes from `useTrackLayout`,
 * gestures from `useTimelineGestures`; this component is the markup.
 */
export function TimelineTracks<T>({
  selectedId,
  onSelect,
  totalCount,
  onJumpToNearest,
}: TimelineTracksProps<T>) {
  const { timeWindow, setTimeWindow, items, format } = useTimelineContext<T>();

  const tickFormatter = React.useMemo(
    () => makeTickFormatter(format),
    [format],
  );

  const containerRef = React.useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);

  const { layout, ticks, labelledGaps } = useTrackLayout(
    items.data,
    timeWindow,
    selectedId,
    width,
    tickFormatter,
  );

  const { dragging, handlers } = useTimelineGestures(containerRef, {
    timeWindow,
    setTimeWindow,
    onTapEmpty: () => onSelect?.(null),
  });

  const [hover, setHover] = React.useState<{
    item: TimelineTrackItem<T>;
    bar: HTMLButtonElement;
  } | null>(null);

  const [openClusterId, setOpenClusterId] = React.useState<string | null>(null);

  // Say "None in span" only when the window holds no item but the timeline has some.
  const empty =
    items.status === "success" &&
    layout !== null &&
    noneInSpan(layout.visible.length, totalCount);

  const at = (time: number) => percent(fractionOf(timeWindow, time));

  return (
    <div
      role="group"
      aria-label="Timeline"
      className={classNames(styles.tracks, { [styles.dragging]: dragging })}
      style={
        { "--track-count": layout?.trackCount ?? 1 } as React.CSSProperties
      }
    >
      {/* The plot is the frame every time-positioned child and the gesture maths share; the inset around it is the parent's padding. */}
      <div
        ref={containerRef}
        className={styles.plot}
        {...handlers}
        onPointerDown={(event) => {
          setHover(null);
          handlers.onPointerDown(event);
        }}
      >
        <div className={styles.axis} aria-hidden="true">
          {ticks.map((tick) => (
            <div
              key={tick.time}
              className={classNames(styles.tick, {
                [styles.tickMajor]: tick.major,
              })}
              style={{ left: at(tick.time) }}
            >
              <span className={styles.tickLabel}>{tick.label}</span>
            </div>
          ))}
        </div>

        {layout && (
          <div className={styles.lanes}>
            {ticks
              .filter((tick) => tick.major)
              .map((tick) => (
                <div
                  key={tick.time}
                  className={styles.gridline}
                  style={{ left: at(tick.time) }}
                  aria-hidden="true"
                />
              ))}
            {labelledGaps.map((gap) => (
              <div
                key={gap.start}
                className={styles.gap}
                style={{
                  left: at(gap.start),
                  width: percent(
                    fractionOf(timeWindow, gap.end) -
                      fractionOf(timeWindow, gap.start),
                  ),
                }}
              >
                Idle {formatIdle(gap.end - gap.start)}
              </div>
            ))}
          </div>
        )}

        {layout?.clips.map((clip) =>
          clip.kind === "clip" ? (
            <TrackBar
              key={clip.item.id}
              clip={clip}
              selected={clip.item.id === selectedId}
              format={format}
              onClick={() => onSelect?.(clip.item)}
              onHover={(bar) => setHover(bar ? { item: clip.item, bar } : null)}
            />
          ) : (
            <TrackCluster
              key={clip.id}
              clip={clip}
              format={format}
              open={openClusterId === clip.id}
              onOpenChange={(open) => setOpenClusterId(open ? clip.id : null)}
              onPick={(item) => {
                setOpenClusterId(null);
                onSelect?.(item);
              }}
              onZoomToFit={() => {
                setOpenClusterId(null);
                setTimeWindow(fitWindow(clip.startTime, clip.endTime));
              }}
            />
          ),
        )}
      </div>

      {empty && <NoSessionsInSpan onJumpToNearest={onJumpToNearest} />}

      <TrackHoverCard
        hover={hover}
        format={format}
        onClose={() => setHover(null)}
      />
    </div>
  );
}
